import { Case, CaseStatuses, OrderCaseStatuses, OrderStatuses, Trip } from "@types-runway/case";
import { deriveOrderPaymentState, OrderPaymentState } from "@utils/billing/orderPaymentState";

// The departure's calendar day is read in UTC: the questionnaire stores the
// picked day at local noon, which falls on the same UTC day. A missing or
// invalid departure is never ahead.
const isOnOrBeforeDepartureDay = (trip: Trip | undefined, today: Date): boolean => {
  const departure = new Date(trip?.from ?? "");
  const departureDay = Date.UTC(
    departure.getUTCFullYear(),
    departure.getUTCMonth(),
    departure.getUTCDate()
  );
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

  return departureDay >= todayDay;
};

/**
 * Merges case and Curexa order status into the single patient-facing
 * status label shown on the dashboard case card.
 *
 * A dispatched custom-pharmacy case has no Curexa order and no payment
 * step, so it goes straight to "pharmacy processing". The custom pharmacy
 * setting is user-level, so cases fulfilled by Curexa keep their
 * order-driven statuses (and with them, the tracking link).
 */
export const resolveOrderCaseStatus = (
  case_: Case,
  isCustomPharmacy: boolean,
  today: Date = new Date()
): OrderCaseStatuses => {
  const orderStatus = case_.CurexaOrder?.replacement?.status ?? case_.CurexaOrder?.status;

  if (case_.case_status === CaseStatuses.CANCELLED) {
    return OrderCaseStatuses.CANCELLED;
  }

  // Support may cancel an order before its replacement exists, so a cancelled
  // order is only paused until the patient departs.
  if (orderStatus === OrderStatuses.CANCELLED) {
    return isOnOrBeforeDepartureDay(case_.trip, today)
      ? OrderCaseStatuses.PAUSED
      : OrderCaseStatuses.CANCELLED;
  }

  if (case_.case_status !== CaseStatuses.COMPLETED) {
    return OrderCaseStatuses.PHYSICIAN_REVIEW;
  }

  const hasPrescriptions = Boolean(case_.conditions[0]);
  if (orderStatus === OrderStatuses.COMPLETED || !hasPrescriptions) {
    return OrderCaseStatuses.COMPLETE;
  }

  if (isCustomPharmacy && !orderStatus) {
    return OrderCaseStatuses.PHARMACY_PROCESSING;
  }

  const paymentState = deriveOrderPaymentState(case_);
  if (paymentState === OrderPaymentState.PAYMENT_FAILED) {
    return OrderCaseStatuses.DECLINED_PAYMENT;
  }
  if (paymentState === OrderPaymentState.PAID) {
    return OrderCaseStatuses.PHARMACY_PROCESSING;
  }

  if (!orderStatus) {
    return OrderCaseStatuses.PENDING_PAYMENT;
  }

  if (orderStatus === OrderStatuses.OUT_FOR_DELIVERY) {
    return OrderCaseStatuses.SHIPPED;
  }

  return OrderCaseStatuses.PHARMACY_PROCESSING;
};
